"use client";

import {
  COOKIE_KEY_HLW_CONSENT,
  deleteCookie,
  getCookie,
  SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS,
  SESSION_STORAGE_KEY_PINTEREST_CLICK_ID,
  setCookie,
  useCookie,
  useIsKidAccount,
  useRequestGeolocation,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { DEFAULT_CONSENT_OPTIONS_STRICT } from "../constants/defaultConsentOptionsStrict.constants";
import type { ConsentContext } from "../lib/consentContext";
import { consentContext } from "../lib/consentContext";
import { Consent } from "../types/enums/consent.enum";
import { handleConsentFromGeolocation } from "../utils/handleConsentFromGeolocation";

const GOOGLE_CLICK_ID_PARAM_KEYS = ["gclid", "wbraid", "gbraid"] as const;

/** Pinterest appends `epik` to the landing URL; its tag (if present) mirrors it
 * into the first-party `_epik` cookie. We persist whichever we find so delayed
 * conversions can still be attributed. */
const PINTEREST_CLICK_ID_PARAM_KEY = "epik";
const PINTEREST_CLICK_ID_COOKIE_KEY = "_epik";

export type ConsentProviderProps = Omit<
  ComponentProps<typeof consentContext.Provider>,
  "value"
>;

export const ConsentProvider = ({
  children,
  ...props
}: ConsentProviderProps) => {
  const [consent, setConsent] = useCookie<number[]>({
    key: COOKIE_KEY_HLW_CONSENT,
    schema: z.number().array(),
  });
  const { query: queryGeolocation } = useRequestGeolocation();
  const { mutationPut: mutationPutMe, query: queryMe } = useRequestMe();
  const { status } = useSession();
  const { isKidAccount } = useIsKidAccount();
  const [isError, setIsError] = useState(false);

  const isConsenting =
    Array.isArray(consent) && consent.includes(Consent.ANALYTICS);
  const isLoading = !consent;

  /** TODO: remove/improve when ConsentModal/CookieManager are improved */
  const [isConsentModalVisible, setIsConsentModalVisible] = useState(false);
  const saveConsent = async ({
    consent,
  }: {
    consent: ConsentContext["consent"];
  }) => {
    if (isKidAccount) return;
    if (status === "authenticated") {
      await mutationPutMe.mutateAsync({ consent });
    }
    setConsent(consent);
  };

  /**
   * If the user does not have consent preferences
   */
  useEffect(() => {
    if (
      (!consent ||
        !(
          Array.isArray(consent) &&
          (consent?.length === 0 ||
            consent?.every((c) => typeof c === "number"))
        )) &&
      !queryGeolocation.isLoading
    ) {
      handleConsentFromGeolocation({
        geolocation: queryGeolocation.data,
        setConsent,
        setIsConsentModalVisible,
      });

      let gpc = false;
      if (typeof navigator !== "undefined") {
        // @ts-ignore -- this is what happens when you implement things that aren't really standards
        gpc = !!navigator.globalPrivacyControl;
      }

      if (gpc) setConsent(DEFAULT_CONSENT_OPTIONS_STRICT);
    }
  }, [consent, queryGeolocation.data, queryGeolocation.isLoading]);

  /** Sync any inconsistent consents */
  useEffect(() => {
    if (queryMe.data?.consent) {
      const backendConsent = queryMe.data.consent.filter(
        (c) => c !== Consent.NECESSARY,
      );
      if (
        (consent &&
          JSON.stringify([...consent].sort()) !==
            JSON.stringify([...backendConsent].sort())) ||
        !consent
      ) {
        setConsent(backendConsent);
      }
    } else if (consent && status === "authenticated" && !isKidAccount) {
      mutationPutMe.mutate({ consent });
    }
  }, [consent, queryMe.data?.consent, isKidAccount]);

  useEffect(() => {
    if (!Array.isArray(consent)) return;

    const hasAdvertisingConsent = consent.includes(Consent.ADVERTISING);
    const urlParams = new URLSearchParams(window.location.search);
    const storedClickIds = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS) ?? "{}",
    ) as Partial<Record<(typeof GOOGLE_CLICK_ID_PARAM_KEYS)[number], string>>;

    const clickIds = GOOGLE_CLICK_ID_PARAM_KEYS.reduce<
      Partial<Record<(typeof GOOGLE_CLICK_ID_PARAM_KEYS)[number], string>>
    >((acc, key) => {
      const value = urlParams.get(key) ?? storedClickIds[key];

      if (value) acc[key] = value;

      return acc;
    }, {});

    sessionStorage.setItem(
      SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS,
      JSON.stringify(clickIds),
    );

    const epik =
      urlParams.get(PINTEREST_CLICK_ID_PARAM_KEY) ??
      sessionStorage.getItem(SESSION_STORAGE_KEY_PINTEREST_CLICK_ID) ??
      getCookie<string>({ key: PINTEREST_CLICK_ID_COOKIE_KEY });

    if (epik)
      sessionStorage.setItem(SESSION_STORAGE_KEY_PINTEREST_CLICK_ID, epik);

    if (!hasAdvertisingConsent) {
      GOOGLE_CLICK_ID_PARAM_KEYS.forEach((key) => {
        deleteCookie({ key, options: { domain: ".hallow.com", path: "/" } });
      });
      deleteCookie({
        key: PINTEREST_CLICK_ID_COOKIE_KEY,
        options: { domain: ".hallow.com", path: "/" },
      });
      return;
    }

    GOOGLE_CLICK_ID_PARAM_KEYS.forEach((key) => {
      const value = clickIds[key];

      if (!value) return;

      setCookie({
        key,
        domain: ".hallow.com",
        options: { expires: 90, sameSite: "lax" },
        value,
      });
    });

    if (epik)
      setCookie({
        key: PINTEREST_CLICK_ID_COOKIE_KEY,
        domain: ".hallow.com",
        options: { expires: 90, sameSite: "lax" },
        value: epik,
      });
  }, [consent]);

  return (
    <consentContext.Provider
      value={{
        consent: (consent ?? []).filter((c) => c !== Consent.NECESSARY),
        isConsenting,
        isLoading,
        isError,
        isConsentModalVisible,
        saveConsent,
        setConsent,
        setIsError,
        setIsConsentModalVisible,
      }}
      {...props}
    >
      {children}
    </consentContext.Provider>
  );
};
